import {
  getToken,
  removeThreeLetterCode,
  removeToken,
  setEmail,
} from '@fukuri-admins/utils/common/cookies';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopLink } from '../../utils/getLinks';

export type UserInfo = {
  company_admin_user: {
    id: string;
    full_name: string;
    email: string;
  };
};

type AuthContextType = {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  isLoading: boolean;
  signIn: (userInfo: UserInfo) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const initializeAuth = async () => {
    try {
      // 初期化時にトークンの存在確認
      const token = getToken();
      if (token) {
        setIsAuthenticated(true);
      }
    } catch {
      removeToken();
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  const signIn = (user: UserInfo) => {
    setIsAuthenticated(true);
    setUserInfo(user);
    setEmail(user.company_admin_user.email);
  };

  const signOut = () => {
    removeToken();
    removeThreeLetterCode();
    setIsAuthenticated(false);
    setUserInfo(null);
    navigate(getTopLink());
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userInfo,
        isLoading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
